import type { SummaryState, SummaryAction } from "./types";

export const initialState: SummaryState = {
  summaries: {},
  loadingStates: {},
  errors: {},
  streamingStates: {},
};

/**
 * How many summaries the provider keeps.
 *
 * `AISummaryProvider` sits app-wide and never bulk-clears on its own — only an
 * explicit CLEAR_SUMMARY/CLEAR_ALL removes anything — while every entity the
 * user summarizes adds a cacheKey holding the FULL summary text plus three
 * parallel flag entries. A day of clicking through contacts, companies and
 * deals therefore retains every summary generated, none of which is on screen.
 *
 * 25 is sized for how the thing is actually used: a summary is read once, on
 * the detail page that generated it, and the value of retaining it is
 * navigating back within the same session. 25 covers a long back-and-forth
 * across a working set of entities and still bounds retention at tens of KB
 * rather than the whole session's output.
 *
 * NOT a `boundedCache` here on purpose: this is reducer state, and a reducer
 * must be a pure function of (state, action) — a class instance in state would
 * mutate outside the dispatch cycle, break React's referential-equality
 * bail-out, and make the eviction unreproducible from the action log.
 */
const MAX_RETAINED_SUMMARIES = 25;

/** Copy `source` without the doomed keys. Pure; allocates a new record. */
function withoutKeys<T>(
  source: Record<string, T>,
  doomed: ReadonlySet<string>
): Record<string, T> {
  const next: Record<string, T> = {};
  for (const [key, value] of Object.entries(source)) {
    if (!doomed.has(key)) next[key] = value;
  }
  return next;
}

/**
 * Drop the oldest summaries until at most MAX_RETAINED_SUMMARIES remain.
 *
 * "Oldest" is first-inserted: string keys keep insertion order in a JS object,
 * and a spread re-write leaves an existing key where it was, so regenerating a
 * summary does not renew its place in the queue. That is deliberate — it makes
 * eviction a pure function of the action sequence, with no access-time
 * bookkeeping to keep in the state.
 *
 * The doomed keys are removed from ALL FOUR records, not just `summaries`: the
 * flag maps are keyed identically, so evicting only the text would leave three
 * entries per summary behind and turn one leak into three smaller ones.
 */
function capRetainedSummaries(state: SummaryState): SummaryState {
  const keys = Object.keys(state.summaries);
  if (keys.length <= MAX_RETAINED_SUMMARIES) return state;

  const doomed = new Set(keys.slice(0, keys.length - MAX_RETAINED_SUMMARIES));
  return {
    summaries: withoutKeys(state.summaries, doomed),
    loadingStates: withoutKeys(state.loadingStates, doomed),
    errors: withoutKeys(state.errors, doomed),
    streamingStates: withoutKeys(state.streamingStates, doomed),
  };
}

export function summaryReducer(state: SummaryState, action: SummaryAction): SummaryState {
  switch (action.type) {
    case "START_LOADING":
      return {
        ...state,
        loadingStates: { ...state.loadingStates, [action.cacheKey]: true },
        errors: { ...state.errors, [action.cacheKey]: "" },
        streamingStates: { ...state.streamingStates, [action.cacheKey]: false },
      };

    case "START_STREAMING":
      return {
        ...state,
        streamingStates: { ...state.streamingStates, [action.cacheKey]: true },
        loadingStates: { ...state.loadingStates, [action.cacheKey]: false },
      };

    case "UPDATE_CONTENT": {
      const existingSummary = state.summaries[action.cacheKey];
      if (existingSummary && existingSummary.content === action.content) {
        return state;
      }
      return {
        ...state,
        summaries: {
          ...state.summaries,
          [action.cacheKey]: {
            ...state.summaries[action.cacheKey],
            content: action.content,
          },
        },
      };
    }

    case "SET_SUMMARY":
      // The only branch that adds retained CONTENT, so it is the only one that
      // needs to enforce the cap.
      return capRetainedSummaries({
        ...state,
        summaries: { ...state.summaries, [action.cacheKey]: action.summary },
        loadingStates: { ...state.loadingStates, [action.cacheKey]: false },
        streamingStates: { ...state.streamingStates, [action.cacheKey]: false },
        errors: { ...state.errors, [action.cacheKey]: "" },
      });

    case "SET_ERROR":
      return {
        ...state,
        errors: { ...state.errors, [action.cacheKey]: action.error },
        loadingStates: { ...state.loadingStates, [action.cacheKey]: false },
        streamingStates: { ...state.streamingStates, [action.cacheKey]: false },
      };

    case "CLEAR_ERROR":
      return {
        ...state,
        errors: { ...state.errors, [action.cacheKey]: "" },
      };

    case "CLEAR_SUMMARY": {
      const newSummaries = { ...state.summaries };
      const newLoadingStates = { ...state.loadingStates };
      const newErrors = { ...state.errors };
      const newStreamingStates = { ...state.streamingStates };

      delete newSummaries[action.cacheKey];
      delete newLoadingStates[action.cacheKey];
      delete newErrors[action.cacheKey];
      delete newStreamingStates[action.cacheKey];

      return {
        summaries: newSummaries,
        loadingStates: newLoadingStates,
        errors: newErrors,
        streamingStates: newStreamingStates,
      };
    }

    case "CLEAR_ALL":
      return initialState;

    default:
      return state;
  }
}
